/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { floatingLayer } from '@/components/ui/layer';

/**
 * A menu hung off the control that opened it.
 *
 * Portalled to the floating layer inside `<main>` (`layer.ts`) so a card with
 * `overflow: hidden` cannot clip it and it still sits in a landmark, and
 * positioned from the trigger's own rectangle so it stays put when the page
 * behind it scrolls. Escape and a click outside both close it, and focus goes
 * back to the trigger — a menu you can open with the keyboard and not leave is
 * a trap.
 *
 * It keeps the promise `role="menu"` makes to a keyboard (the WAI-ARIA menu
 * button pattern): opening moves focus to the first item -- the last, when
 * opened with ArrowUp -- ArrowUp and ArrowDown move between items and wrap,
 * Home and End go to either end, and Tab closes it and carries on from the
 * trigger. Portalled to the end of `<main>`, the panel would otherwise come
 * after everything else on the page in the tab order.
 */
//: Taller than any menu here, so one only opens upward where it truly has to.
//: A panel that turns out taller than the space it was given scrolls; `.menu`
//: caps its height against the window.
const ROOM_FOR_A_MENU = 280;

/** What counts as an item: what arrow keys move between. */
const ITEMS =
  '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled), [role="menuitemcheckbox"]:not(:disabled)';

function itemsOf(panel: HTMLElement | null): HTMLElement[] {
  return panel ? Array.from(panel.querySelectorAll<HTMLElement>(ITEMS)) : [];
}

export function Menu({
  label,
  trigger,
  children,
  align = 'end',
  width = 240,
  onOpenChange,
}: {
  label: string;
  trigger: (props: {
    ref: (node: HTMLButtonElement | null) => void;
    onClick: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string | undefined;
  }) => React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  align?: 'start' | 'end';
  /** How wide the panel is laid out, so it is placed by its real edge. */
  width?: number;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  //: Anchored by the top edge, or by the bottom edge when it opens upward.
  const [box, setBox] = useState<{ top?: number; bottom?: number; left: number }>({
    top: 0,
    left: 0,
  });
  // The trigger and the panel are held as state, not refs: `close` is handed
  // to the caller during render, and a ref read there has not settled yet.
  const [trigger_, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const id = useId();

  // Read outside render: a ref touched while rendering will not have settled.
  /**
   * Where the panel goes, measured when it is opened rather than after it
   * renders: the trigger's rectangle is known at click time, and measuring in
   * an effect would paint the menu once in the wrong place first.
   */
  const place = (node: HTMLButtonElement) => {
    const rect = node.getBoundingClientRect();
    const left = align === 'end' ? rect.right - width : rect.left;
    // Never off the left edge, never past the right one.
    const x = Math.max(10, Math.min(left, window.innerWidth - width - 10));

    // Upward when there is not room beneath and there is more of it above --
    // the account menu's trigger is the last thing in the rail, and a panel
    // that only ever opened downward hung off the bottom of the window.
    //
    // Anchored by its *bottom* edge in that case, which is what makes this
    // possible without measuring: the panel's height is not knowable until it
    // has rendered, but the edge it grows away from is known at click time.
    const below = window.innerHeight - rect.bottom;
    if (below < ROOM_FOR_A_MENU && rect.top > below) {
      setBox({ bottom: window.innerHeight - rect.top + 8, left: x });
      return;
    }

    setBox({ top: rect.bottom + 8, left: x });
  };

  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
    trigger_?.focus();
  }, [onOpenChange, trigger_]);

  // Which end focus starts at once the panel is there: the first item, or the
  // last when ArrowUp opened it.
  const [startAt, setStartAt] = useState<'first' | 'last'>('first');

  useEffect(() => {
    if (!open || !panel) return;
    const items = itemsOf(panel);
    // One tab stop for the whole menu: the arrows move within it.
    for (const item of items) item.tabIndex = -1;
    const first = startAt === 'last' ? items.at(-1) : items[0];
    (first ?? panel).focus();
  }, [open, panel, startAt]);

  const openMenu = (from: 'first' | 'last') => {
    setStartAt(from);
    if (trigger_) place(trigger_);
    setOpen(true);
    onOpenChange?.(true);
  };

  /** Arrows, Home and End move between items; Tab leaves, closing it. */
  const onPanelKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = itemsOf(panel);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (index: number) => {
      event.preventDefault();
      items[(index + items.length) % items.length]?.focus();
    };
    if (event.key === 'Tab') {
      // Focus back on the trigger first, so the browser's own Tab carries on
      // from where the menu hangs rather than from the end of the page.
      setOpen(false);
      onOpenChange?.(false);
      trigger_?.focus();
      return;
    }
    if (items.length === 0) return;
    if (event.key === 'ArrowDown') go(at + 1);
    else if (event.key === 'ArrowUp') go(at < 0 ? items.length - 1 : at - 1);
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(items.length - 1);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panel?.contains(target) || trigger_?.contains(target)) return;
      setOpen(false);
      onOpenChange?.(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  });

  return (
    <>
      {trigger({
        ref: setTrigger,
        onClick: () => {
          if (open) {
            setOpen(false);
            onOpenChange?.(false);
          } else {
            openMenu('first');
          }
        },
        onKeyDown: (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openMenu(event.key === 'ArrowUp' ? 'last' : 'first');
          }
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
      })}
      {open
        ? createPortal(
            <div
              className="menu"
              role="menu"
              aria-label={label}
              id={id}
              ref={setPanel}
              tabIndex={-1}
              onKeyDown={onPanelKey}
              // Inline because it is measured: where the trigger sits, and how
              // wide it is, are known only once it has rendered (UI-4).
              style={{
                top: box.top,
                bottom: box.bottom,
                left: box.left,
                position: 'fixed',
                // Never wider than the window: a min-width beats the
                // stylesheet's max-width, so it is capped here too.
                minWidth: `min(${String(width)}px, calc(100vw - var(--menu-gutter)))`,
              }}
            >
              {children(close)}
            </div>,
            floatingLayer(trigger_),
          )
        : null}
    </>
  );
}

/** One line of a menu: an icon, a name, and sometimes a sentence under it. */
export function MenuItem({
  onClick,
  icon,
  title,
  detail,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  detail?: string;
}) {
  return (
    <button type="button" role="menuitem" tabIndex={-1} onClick={onClick}>
      {icon}
      <span>
        {detail ? <b>{title}</b> : title}
        {detail ? <small>{detail}</small> : null}
      </span>
    </button>
  );
}
